import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Chip,
  Autocomplete,
  TextField,
  Box,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { Close, KeyboardArrowDown } from "@material-ui/icons";
import axios from "axios";
import {
  FormLabel,
  ModalError,
  ModalSuccess,
  ModalWarning,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "components";
import {
  ContentState,
  convertFromHTML,
  EditorState,
  Modifier,
  SelectionState,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { hardBaseUrl } from "services/urlConstant";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const VARIABLE_OPTIONS = [
  { label: "Jumlah Traffic", variable: "Jumlah Traffic" },
  { label: "Jumlah Revenue", variable: "Revenue" },
  { label: "Jumlah Lagu", variable: "Jumlah Lagu" },
  { label: "Pesan Error 1", variable: "Pesan Error 1" },
  { label: "Pesan Error 2", variable: "Pesan Error 2" },
];

const ModalEmailNotification = ({
  open,
  onClose,
  data,
  mode = "detail", // "detail" or "edit"
  onSaved,
  onSwitchToEdit,
}) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const isEdit = mode === "edit";

  const [textEditor, setTextEditor] = useState(() => EditorState.createEmpty());
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [anchorElVariables, setAnchorElVariables] = useState(null);
  const [subject, setSubject] = useState("");
  const [recipients, setRecipients] = useState(["zidane@gmail.com", "b@mail.com"]);
  const [recipientInput, setRecipientInput] = useState("");
  const [recipientError, setRecipientError] = useState("");

  const [ccList, setCcList] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [ccError, setCcError] = useState("");

  const [variableOptions, setVariableOptions] = useState(VARIABLE_OPTIONS);
  const [loadingVariables, setLoadingVariables] = useState(false);

  const lastSelectionRef = useRef(null);
  const editorRef = useRef(null);

  const applyData = item => {
    setSubject(item?.subject || "");
    setRecipientError("");
    setRecipientInput("");
    setCcError("");
    setCcInput("");

    const toEmails = Array.isArray(item?.to_emails)
      ? item.to_emails
      : Array.isArray(item?.recipients)
      ? item.recipients
      : typeof item?.recipient === "string" && item.recipient.trim()
      ? item.recipient.split(",").map(r => r.trim()).filter(Boolean)
      : ["zidane@gmail.com", "b@mail.com"];
    setRecipients(toEmails);

    const ccEmails = Array.isArray(item?.cc_emails)
      ? item.cc_emails
      : Array.isArray(item?.ccList)
      ? item.ccList
      : typeof item?.cc === "string" && item.cc.trim()
      ? item.cc.split(",").map(r => r.trim()).filter(Boolean)
      : [];
    setCcList(ccEmails);

    const contentHtml =
      item?.body || item?.content || item?.template_content || "";
    if (contentHtml) {
      const blocksFromHTML = convertFromHTML(contentHtml);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      const initialEditor = EditorState.createWithContent(contentState);
      setTextEditor(initialEditor);
      lastSelectionRef.current = initialEditor.getSelection();
    } else {
      const emptyEditor = EditorState.createEmpty();
      setTextEditor(emptyEditor);
      lastSelectionRef.current = emptyEditor.getSelection();
    }
  };

  const getVariables = async item => {
    const rawKey =
      item?.event_key ||
      item?.variable_key ||
      item?.key ||
      item?.code ||
      item?.slug ||
      item?.type ||
      "";
    const variableKey = rawKey
      ? String(rawKey).toLowerCase().trim().replace(/[\s-]+/g, "_")
      : "dsrf_success";

    try {
      setLoadingVariables(true);
      const res = await axios.get(
        `${hardBaseUrl}/system-email-notification/variables/${variableKey}`,
        { headers }
      );
      const resData = res?.data?.data || res?.data;
      if (Array.isArray(resData) && resData.length > 0) {
        const formatted = resData.map(v => {
          if (typeof v === "string") {
            const clean = v.replace(/[{}]/g, "").trim();
            return { label: clean, variable: clean };
          }
          const label =
            v?.label || v?.name || v?.title || v?.variable || v?.key || "";
          const variable =
            v?.variable || v?.key || v?.value || v?.name || v?.label || "";
          const clean = String(variable).replace(/[{}]/g, "").trim();
          return { label: label || clean, variable: clean };
        });
        setVariableOptions(formatted);
      } else {
        setVariableOptions(VARIABLE_OPTIONS);
      }
    } catch (error) {
      setVariableOptions(VARIABLE_OPTIONS);
    } finally {
      setLoadingVariables(false);
    }
  };

  const getDetail = async id => {
    try {
      setLoadingDetail(true);
      const res = await axios.get(
        `${hardBaseUrl}/system-email-notification/detail/${id}`,
        { headers }
      );
      const resData = res?.data?.data || res?.data;
      if (resData) {
        applyData(resData);
        getVariables(resData);
      } else {
        applyData(data);
        getVariables(data);
      }
    } catch (error) {
      applyData(data);
      getVariables(data);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (open && data?.id) {
      getDetail(data.id);
    } else if (data) {
      applyData(data);
      getVariables(data);
    }
  }, [data, open, mode]);

  const handleEditorChange = newEditorState => {
    setTextEditor(newEditorState);
    const selection = newEditorState.getSelection();
    if (selection) {
      lastSelectionRef.current = selection;
    }
  };

  const handleOpenVariablesMenu = event => {
    setAnchorElVariables(event.currentTarget);
  };

  const handleCloseVariablesMenu = () => {
    setAnchorElVariables(null);
  };

  const handleInsertVariable = variableName => {
    const textToInsert = `{{${variableName}}}`;
    const currentContent = textEditor.getCurrentContent();
    let targetSelection = lastSelectionRef.current || textEditor.getSelection();

    if (!targetSelection || !targetSelection.getAnchorKey()) {
      const lastBlock = currentContent.getLastBlock();
      const length = lastBlock.getLength();
      targetSelection = SelectionState.createEmpty(lastBlock.getKey()).merge({
        anchorKey: lastBlock.getKey(),
        anchorOffset: length,
        focusKey: lastBlock.getKey(),
        focusOffset: length,
        isBackward: false,
        hasFocus: true,
      });
    }

    const newContent = Modifier.insertText(
      currentContent,
      targetSelection,
      textToInsert
    );

    const blockKey = targetSelection.getStartKey();
    const newOffset = targetSelection.getStartOffset() + textToInsert.length;
    const newSelection = SelectionState.createEmpty(blockKey).merge({
      anchorKey: blockKey,
      anchorOffset: newOffset,
      focusKey: blockKey,
      focusOffset: newOffset,
      isBackward: false,
      hasFocus: true,
    });

    let newEditorState = EditorState.push(
      textEditor,
      newContent,
      "insert-characters"
    );

    newEditorState = EditorState.forceSelection(newEditorState, newSelection);

    setTextEditor(newEditorState);
    lastSelectionRef.current = newSelection;
    handleCloseVariablesMenu();

    setTimeout(() => {
      if (editorRef.current && editorRef.current.focusEditor) {
        editorRef.current.focusEditor();
      }
    }, 50);
  };

  const handleRecipientChange = (_, newValue) => {
    setRecipientError("");
    const cleanedValues = [];
    let hasInvalid = false;

    newValue.forEach(val => {
      const email = typeof val === "string" ? val.trim() : "";
      if (email) {
        if (EMAIL_REGEX.test(email)) {
          if (!cleanedValues.includes(email)) {
            cleanedValues.push(email);
          }
        } else {
          hasInvalid = true;
        }
      }
    });

    if (hasInvalid) {
      setRecipientError("Format email tidak valid (contoh: name@domain.com)");
    }
    setRecipients(cleanedValues);
  };

  const handleKeyDownRecipient = event => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const email = recipientInput.trim().replace(/,/g, "");
      if (email) {
        if (EMAIL_REGEX.test(email)) {
          if (!recipients.includes(email)) {
            setRecipients(prev => [...prev, email]);
          }
          setRecipientInput("");
          setRecipientError("");
        } else {
          setRecipientError("Format email tidak valid (contoh: name@domain.com)");
        }
      }
    }
  };

  const handleCcChange = (_, newValue) => {
    setCcError("");
    const cleanedValues = [];
    let hasInvalid = false;

    newValue.forEach(val => {
      const email = typeof val === "string" ? val.trim() : "";
      if (email) {
        if (EMAIL_REGEX.test(email)) {
          if (!cleanedValues.includes(email)) {
            cleanedValues.push(email);
          }
        } else {
          hasInvalid = true;
        }
      }
    });

    if (hasInvalid) {
      setCcError("Format email CC tidak valid (contoh: name@domain.com)");
    }
    setCcList(cleanedValues);
  };

  const handleKeyDownCc = event => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      const email = ccInput.trim().replace(/,/g, "");
      if (email) {
        if (EMAIL_REGEX.test(email)) {
          if (!ccList.includes(email)) {
            setCcList(prev => [...prev, email]);
          }
          setCcInput("");
          setCcError("");
        } else {
          setCcError("Format email CC tidak valid (contoh: name@domain.com)");
        }
      }
    }
  };

  const handleSubmitSave = () => {
    let currentRecipients = [...recipients];
    if (recipientInput.trim()) {
      const typedEmail = recipientInput.trim().replace(/,/g, "");
      if (EMAIL_REGEX.test(typedEmail)) {
        if (!currentRecipients.includes(typedEmail)) {
          currentRecipients.push(typedEmail);
        }
        setRecipients(currentRecipients);
        setRecipientInput("");
        setRecipientError("");
      } else {
        setRecipientError("Format email tidak valid (contoh: name@domain.com)");
        return;
      }
    }

    let currentCcList = [...ccList];
    if (ccInput.trim()) {
      const typedCc = ccInput.trim().replace(/,/g, "");
      if (EMAIL_REGEX.test(typedCc)) {
        if (!currentCcList.includes(typedCc)) {
          currentCcList.push(typedCc);
        }
        setCcList(currentCcList);
        setCcInput("");
        setCcError("");
      } else {
        setCcError("Format email CC tidak valid (contoh: name@domain.com)");
        return;
      }
    }

    const invalidRecipient = currentRecipients.find(e => !EMAIL_REGEX.test(e));
    if (invalidRecipient) {
      setRecipientError(`Email '${invalidRecipient}' formatnya tidak valid`);
      return;
    }

    const invalidCc = currentCcList.find(e => !EMAIL_REGEX.test(e));
    if (invalidCc) {
      setCcError(`Email CC '${invalidCc}' formatnya tidak valid`);
      return;
    }

    ModalWarning(
      "Are you sure you want to save these changes?",
      "Save Changes"
    ).then(res => {
      if (res?.isConfirmed) {
        executeSave(currentRecipients, currentCcList);
      }
    });
  };

  const executeSave = async (
    finalRecipients = recipients,
    finalCcList = ccList
  ) => {
    const content = stateToHTML(textEditor.getCurrentContent());
    const payload = {
      subject: subject || "",
      to_emails: finalRecipients,
      cc_emails: finalCcList,
      body: content,
    };

    const localUpdatedItem = {
      ...data,
      ...payload,
      recipient: finalRecipients.join(", "),
      recipients: finalRecipients,
      cc: finalCcList.join(", "),
      ccList: finalCcList,
      content,
    };

    try {
      setLoadingButton(true);
      if (data?.id) {
        await axios.put(
          `${hardBaseUrl}/system-email-notification/update/${data.id}`,
          payload,
          { headers }
        );
      }
      ModalSuccess("Successfully saved changes");
      if (onSaved) onSaved(localUpdatedItem);
      onClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to save changes";
      ModalError(errorMessage);
    } finally {
      setLoadingButton(false);
    }
  };

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: "8px",
          maxWidth: "760px",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} fontSize="18px">
          {isEdit ? "Edit Email Notification" : "Email Notification Detail"}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {loadingDetail ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="280px"
          >
            <CircularProgress size={36} />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
          {/* Subject Field */}
          <Grid item xs={12}>
            <FormLabel label="Subject" required={isEdit} />
            {isEdit ? (
              <TextInput
                placeholder="Input subject (e.g. DSRF Proses sukses)"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                width="100%"
              />
            ) : (
              <Typography variant="body1" fontWeight={500}>
                {subject || "-"}
              </Typography>
            )}
          </Grid>

          {/* Recipient Field */}
          <Grid item xs={12}>
            <FormLabel label="Recipient" required={isEdit} />
            {isEdit ? (
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={recipients}
                  inputValue={recipientInput}
                  onInputChange={(_, newInputValue) => {
                    setRecipientInput(newInputValue);
                    if (recipientError) setRecipientError("");
                  }}
                  onChange={handleRecipientChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={option}
                        size="small"
                        sx={{
                          borderRadius: "16px",
                          borderColor: "#D1D5DB",
                          backgroundColor: "#F9FAFB",
                          fontWeight: 500,
                          mr: 0.5,
                          "& .MuiChip-deleteIcon": {
                            fontSize: "16px",
                            color: "#6B7280",
                            "&:hover": {
                              color: "#111827",
                            },
                          },
                        }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        recipients.length === 0
                          ? "Masukkan email recipient (e.g. user@domain.com)..."
                          : ""
                      }
                      variant="outlined"
                      size="small"
                      error={Boolean(recipientError)}
                      onKeyDown={handleKeyDownRecipient}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          backgroundColor: "#FFFFFF",
                        },
                      }}
                    />
                  )}
                />
                {recipientError && (
                  <FormHelperText error sx={{ ml: 1, mt: 0.5, fontSize: "12px" }}>
                    {recipientError}
                  </FormHelperText>
                )}
              </Box>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {recipients.map((email, idx) => (
                  <Chip
                    key={idx}
                    variant="outlined"
                    label={email}
                    size="small"
                    sx={{
                      borderRadius: "16px",
                      borderColor: "#D1D5DB",
                      backgroundColor: "#F9FAFB",
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>

          {/* CC Field */}
          <Grid item xs={12}>
            <FormLabel label="CC" />
            {isEdit ? (
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={ccList}
                  inputValue={ccInput}
                  onInputChange={(_, newInputValue) => {
                    setCcInput(newInputValue);
                    if (ccError) setCcError("");
                  }}
                  onChange={handleCcChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={option}
                        size="small"
                        sx={{
                          borderRadius: "16px",
                          borderColor: "#D1D5DB",
                          backgroundColor: "#F9FAFB",
                          fontWeight: 500,
                          mr: 0.5,
                          "& .MuiChip-deleteIcon": {
                            fontSize: "16px",
                            color: "#6B7280",
                            "&:hover": {
                              color: "#111827",
                            },
                          },
                        }}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder={
                        ccList.length === 0
                          ? "Masukkan email CC (e.g. cc@domain.com)..."
                          : ""
                      }
                      variant="outlined"
                      size="small"
                      error={Boolean(ccError)}
                      onKeyDown={handleKeyDownCc}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "6px",
                          backgroundColor: "#FFFFFF",
                        },
                      }}
                    />
                  )}
                />
                {ccError && (
                  <FormHelperText error sx={{ ml: 1, mt: 0.5, fontSize: "12px" }}>
                    {ccError}
                  </FormHelperText>
                )}
              </Box>
            ) : (
              <Box>
                {ccList.length > 0 ? (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {ccList.map((email, idx) => (
                      <Chip
                        key={idx}
                        variant="outlined"
                        label={email}
                        size="small"
                        sx={{
                          borderRadius: "16px",
                          borderColor: "#D1D5DB",
                          backgroundColor: "#F9FAFB",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    -
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          {/* Announcement / Email Content */}
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <FormLabel label="Announcement Content" required={isEdit} />
              {isEdit && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleOpenVariablesMenu}
                    endIcon={<KeyboardArrowDown />}
                    sx={{
                      textTransform: "none",
                      borderRadius: "6px",
                      borderColor: "#111827",
                      color: "#111827",
                      fontWeight: 600,
                      fontSize: "13px",
                      padding: "4px 12px",
                      "&:hover": {
                        borderColor: "#000",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    + Add Variables
                  </Button>
                  <Menu
                    anchorEl={anchorElVariables}
                    open={Boolean(anchorElVariables)}
                    onClose={handleCloseVariablesMenu}
                    PaperProps={{
                      style: {
                        minWidth: "220px",
                        borderRadius: "6px",
                        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    {loadingVariables ? (
                      <MenuItem disabled sx={{ fontSize: "13px", py: 1.5, justifyContent: "center" }}>
                        <CircularProgress size={18} sx={{ mr: 1 }} /> Loading variables...
                      </MenuItem>
                    ) : variableOptions.length > 0 ? (
                      variableOptions.map((item, idx) => (
                        <MenuItem
                          key={idx}
                          onClick={() => handleInsertVariable(item.variable)}
                          sx={{
                            fontSize: "14px",
                            py: 1,
                            borderBottom:
                              idx !== variableOptions.length - 1
                                ? "1px solid #F3F4F6"
                                : "none",
                            "&:hover": {
                              backgroundColor: "#F9FAFB",
                            },
                          }}
                        >
                          {item.label}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled sx={{ fontSize: "13px", py: 1.5 }}>
                        No variables available
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>

            {isEdit ? (
              <Editor
                editorState={textEditor}
                wrapperClassName="wrapper-class"
                editorClassName="editor-class input_description"
                editorStyle={{
                  height: "28vh",
                  backgroundColor: "white",
                  paddingLeft: "12px",
                  paddingRight: "12px",
                }}
                toolbarClassName="toolbar-class"
                wrapperStyle={{
                  border: "1px solid #E5E7EB",
                  paddingLeft: 0,
                  paddingRight: 0,
                  borderRadius: "6px",
                }}
                onEditorStateChange={handleEditorChange}
                editorRef={ref => {
                  editorRef.current = ref;
                }}
              />
            ) : (
              <Box
                p={2}
                bgcolor="#f9fafb"
                borderRadius="6px"
                border="1px solid #e5e7eb"
                minHeight="120px"
                dangerouslySetInnerHTML={{
                  __html:
                    data?.body ||
                    data?.content ||
                    stateToHTML(textEditor.getCurrentContent()) ||
                    "<p>No content available</p>",
                }}
              />
            )}
          </Grid>
        </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, justifyContent: "space-between" }}>
        {isEdit ? (
          <>
            <SecondaryButton label="Cancel" onClick={onClose} />
            <PrimaryButton
              variant="contained"
              onClick={handleSubmitSave}
              label="Save"
              loading={loadingButton}
            />
          </>
        ) : (
          <>
            {onSwitchToEdit ? (
              <SecondaryButton
                label="Edit"
                onClick={() => {
                  if (onSwitchToEdit) onSwitchToEdit(data);
                }}
              />
            ) : (
              <div />
            )}
            <PrimaryButton
              variant="contained"
              onClick={onClose}
              label="Close"
            />
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalEmailNotification;
